import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ImagePlus, Loader2, X, Search } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg, media } from "@/lib/api";

const CATS = [["decorations", "Decoration"], ["puja-ideas", "Puja Idea"], ["tips", "Devotional Tip"], ["womens", "Women's Corner"]];
const IDEAS = ["small-space-puja", "home-mandir", "balcony-puja", "festive-decoration", "budget-decoration", "diy-decoration", "kids-puja", "traditional-decoration", "modern-decoration"];
const blank = { title: "", caption: "", festival: "", puja_type: "", category: "decorations", idea_category: "", city: "", hashtags: "", how_decorated: "", products_used: "", tip: "" };

function ProductTagger({ tagged, setTagged }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/products", { params: { q, limit: 6 } }).then((r) => setRes(r.data.items)), 250);
    return () => clearTimeout(t);
  }, [q]);
  const toggle = (p) => setTagged((t) => (t.find((x) => x.id === p.id) ? t.filter((x) => x.id !== p.id) : [...t, p]));
  return (
    <div>
      <p className="mb-2 text-sm font-semibold">Tag products you used <span className="font-normal text-[#78716C]">(others can “Shop this look”)</span></p>
      <div className="relative mb-2"><Search className="absolute left-3 top-3 h-4 w-4 text-[#A8A29E]" />
        <input className="input !pl-9" placeholder="Search Brass Diya, Garland…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tag-product-search" /></div>
      <div className="flex flex-wrap gap-2">
        {res.map((p) => (
          <button type="button" key={p.id} onClick={() => toggle(p)} data-testid={`tag-product-${p.slug}`}
            className={`chip ${tagged.find((x) => x.id === p.id) ? "chip-on" : ""}`}>
            <img src={p.image} alt="" className="h-5 w-5 rounded-full object-cover" />{p.name}
          </button>
        ))}
      </div>
      {tagged.length > 0 && <p className="mt-2 text-xs text-[#6B1D2F]">Tagged: {tagged.map((t) => t.name).join(", ")}</p>}
    </div>
  );
}

export default function SharePujaModal() {
  const { sharePuja, setSharePuja, user } = useApp();
  const [f, setF] = useState(blank);
  const [media_, setMedia] = useState([]);
  const [tagged, setTagged] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => { if (sharePuja && !festivals.length) api.get("/festivals").then((r) => setFestivals(r.data)); }, [sharePuja, festivals.length]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (files) => {
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/upload", fd);
        setMedia((m) => [...m, r.data]);
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Please add a title");
    if (!media_.length) return toast.error("Please add at least one photo or video");
    setBusy(true);
    try {
      const body = { ...f, festival: f.festival || null, idea_category: f.idea_category || null, images: media_.filter((m) => m.type === "image").map((m) => m.url),
        video: media_.find((m) => m.type === "video")?.url || null, hashtags: f.hashtags.split(/[\s,]+/).filter(Boolean).map((h) => (h.startsWith("#") ? h : `#${h}`)),
        tagged_products: tagged.map((t) => t.id), city: f.city || user?.city };
      const r = await api.post("/posts", body);
      toast.success("Your puja is shared! 🪔 Thank you for inspiring others.");
      setSharePuja(false); setF(blank); setMedia([]); setTagged([]);
      nav(`/post/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={sharePuja} onOpenChange={setSharePuja}>
      <DialogContent data-testid="share-puja-modal" className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-3xl border-[#E7E0D3] bg-[#FFFDF7]">
        <DialogTitle className="font-display text-2xl text-[#6B1D2F]">Show Us Your Puja Corner</DialogTitle>
        <DialogDescription>Your devotion can inspire someone else.</DialogDescription>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="flex flex-wrap gap-3">
            {media_.map((m, i) => (
              <div key={m.url} className="relative h-24 w-24 overflow-hidden rounded-2xl">
                {m.type === "video" ? <video src={media(m.url)} className="h-full w-full object-cover" /> : <img src={media(m.url)} alt="" className="h-full w-full object-cover" />}
                <button type="button" onClick={() => setMedia(media_.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button>
              </div>
            ))}
            <label data-testid="share-puja-upload" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-[#E5C378] bg-[#FDF8E2] text-xs font-medium text-[#9A7B1C] hover:bg-[#FEF3C7]">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}Photos / Video
              <input type="file" accept="image/*,video/mp4,video/webm" multiple hidden onChange={(e) => upload([...e.target.files])} data-testid="share-puja-file-input" />
            </label>
          </div>
          <input className="input" placeholder="Title e.g. My Diwali Puja Decoration ❤️" value={f.title} onChange={set("title")} data-testid="share-puja-title" />
          <textarea className="input min-h-[80px]" placeholder="Write a caption…" value={f.caption} onChange={set("caption")} data-testid="share-puja-caption" />
          <div className="grid gap-3 sm:grid-cols-3">
            <select className="input" value={f.festival} onChange={set("festival")} data-testid="share-puja-festival">
              <option value="">Festival (optional)</option>{festivals.map((x) => <option key={x.slug} value={x.slug}>{x.name}</option>)}
            </select>
            <select className="input" value={f.category} onChange={set("category")} data-testid="share-puja-category">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select className="input" value={f.idea_category} onChange={set("idea_category")} data-testid="share-puja-idea">
              <option value="">Puja style (optional)</option>{IDEAS.map((v) => <option key={v} value={v}>{v.replace(/-/g, " ")}</option>)}
            </select>
            <input className="input" placeholder="Puja type e.g. Lakshmi Puja" value={f.puja_type} onChange={set("puja_type")} data-testid="share-puja-type" />
            <input className="input" placeholder={`City ${user?.city ? `(${user.city})` : ""}`} value={f.city} onChange={set("city")} data-testid="share-puja-city" />
            <input className="input" placeholder="#diwali #mandir" value={f.hashtags} onChange={set("hashtags")} data-testid="share-puja-hashtags" />
          </div>
          <textarea className="input" placeholder="How did you decorate? (optional)" value={f.how_decorated} onChange={set("how_decorated")} data-testid="share-puja-how" />
          <textarea className="input" placeholder="Share your tip (optional)" value={f.tip} onChange={set("tip")} data-testid="share-puja-tip" />
          <ProductTagger tagged={tagged} setTagged={setTagged} />
          <button disabled={busy} className="btn-maroon w-full !py-3" data-testid="share-puja-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "+ Share Your Puja"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
